import type { DeskStoreEntry } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bot, ChevronLeft, Database, Download, Plug, Save, Sparkles, SquareStack, Store, Trash2, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { InstallDeskDialog, SaveDesksToStoreDialog } from '@/components/desk-store-dialogs';
import { Page, PageHeader } from '@/components/page';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useApiMutation, useWorkspaceId } from '@/lib/hooks';
import { intlLocale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** Desk Store (docs/features/desk-store.md): ready-made desks to install into this workspace, one or a whole set. */
export function DeskStorePage() {
  const workspaceId = useWorkspaceId();
  const entries = useQuery({ queryKey: qk.deskStore(workspaceId), queryFn: () => api.deskStore.list(workspaceId), enabled: !!workspaceId });

  const shipped = entries.data?.filter((e) => e.source === 'deskworks') ?? [];
  const own = entries.data?.filter((e) => e.source === 'account') ?? [];

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/w/$workspaceId/desks" params={{ workspaceId }} className="inline-flex items-center gap-1 hover:text-foreground">
            <ChevronLeft className="size-3.5" /> {t('Desks')}
          </Link>
        }
        title={t('Desk Store')}
        description={t('Ready-made desks with agents, playbook, skills, triggers and tables – one desk or a whole workspace. Install instead of setting it up again; you only connect your own systems.')}
        actions={
          <SaveDesksToStoreDialog workspaceId={workspaceId}>
            <Button variant="outline">
              <Save /> {t('Save desks')}
            </Button>
          </SaveDesksToStoreDialog>
        }
      />
      {entries.isPending ? (
        <LoadingRows rows={4} />
      ) : entries.isError ? (
        <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />
      ) : (
        <div className="grid gap-8">
          <StoreSection title={t('From Deskworks')}>
            {shipped.map((e) => (
              <EntryCard key={e.id} entry={e} workspaceId={workspaceId} />
            ))}
          </StoreSection>
          <StoreSection title={t('Saved in your account')}>
            {own.length ? (
              own.map((e) => <EntryCard key={e.id} entry={e} workspaceId={workspaceId} />)
            ) : (
              <div className="md:col-span-2">
                <EmptyState
                  icon={<Store />}
                  title={t('Nothing saved yet')}
                  description={t('Open a desk that works well and choose “Save to Desk Store”, or save several desks of this workspace with “Save desks”. Every workspace of your account can then install them.')}
                />
              </div>
            )}
          </StoreSection>
        </div>
      )}
    </Page>
  );
}

function StoreSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-3">
      <h2 className="text-sm font-semibold text-muted-foreground">{title}</h2>
      <div className="grid gap-3 md:grid-cols-2">{children}</div>
    </section>
  );
}

function EntryCard({ entry: e, workspaceId }: { entry: DeskStoreEntry; workspaceId: string }) {
  const remove = useApiMutation(() => api.deskStore.remove(workspaceId, e.id), {
    success: t('“{name}” was removed from the Desk Store', { name: e.name }),
    invalidate: [qk.deskStore(workspaceId)],
  });
  const c = e.contents;
  const [only] = c.desks.length === 1 ? c.desks : [];
  return (
    <Card className="flex h-full flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold">{e.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {e.category && <Badge tone="accent">{e.category}</Badge>}
            {!only && <Badge tone="neutral">{t('{n} desks', { n: c.desks.length })}</Badge>}
            <Badge tone="muted">v{e.version}</Badge>
            {e.installs > 0 && (
              <Badge tone="green">{e.installs === 1 ? t('in 1 workspace') : t('in {n} workspaces', { n: e.installs.toLocaleString(intlLocale) })}</Badge>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {e.source === 'account' && (
            <Confirm
              title={t('Remove “{name}” from the Desk Store?', { name: e.name })}
              description={t('Desks installed from it stay as they are.')}
              confirmLabel={t('Remove')}
              destructive
              onConfirm={() => remove.mutateAsync()}
            >
              <Button variant="ghost" size="sm" aria-label={t('Remove from Desk Store')}>
                <Trash2 />
              </Button>
            </Confirm>
          )}
          <InstallDeskDialog workspaceId={workspaceId} entry={e}>
            <Button size="sm">
              <Download /> {t('Install')}
            </Button>
          </InstallDeskDialog>
        </div>
      </div>
      {e.description && <p className="text-sm text-muted-foreground">{e.description}</p>}
      <dl className="mt-auto grid gap-1.5 border-t pt-3 text-xs">
        {only ? (
          <Row icon={<Bot />} label={t('Team')}>
            {only.agents.map((a) => (a.role === 'lead' ? t('{name} (lead)', { name: a.name }) : a.name)).join(', ')}
          </Row>
        ) : (
          <Row icon={<SquareStack />} label={t('Desks')}>
            {c.desks.map((d) => (d.lead ? `${d.name} (${d.lead})` : d.name)).join(', ')}
          </Row>
        )}
        {c.connections.length > 0 && (
          <Row icon={<Plug />} label={t('Connections')}>
            {c.connections.map((x) => (x.permissions.includes('write') ? x.name : t('{name} (read)', { name: x.name }))).join(', ')}
          </Row>
        )}
        {c.skills.length > 0 && (
          <Row icon={<Sparkles />} label={t('Skills')}>
            {c.skills.join(', ')}
          </Row>
        )}
        {c.triggers.length > 0 && (
          <Row icon={<Zap />} label={t('Triggers')}>
            {c.triggers.map((x) => x.name).join(', ')}
          </Row>
        )}
        {c.tables.length > 0 && (
          <Row icon={<Database />} label={t('Tables')}>
            {c.tables.join(', ')}
          </Row>
        )}
      </dl>
      {e.createdByName && <p className="text-xs text-muted-foreground">{t('Saved by {name}', { name: e.createdByName })}</p>}
    </Card>
  );
}

function Row({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[auto_6.5rem_1fr] items-start gap-2">
      <span className="mt-px text-muted-foreground [&>svg]:size-3.5">{icon}</span>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
